import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Dialog from '@zenbusiness/zui/Dialog';
import DialogTitle from '@zenbusiness/zui/DialogTitle';
import IconButton from '@zenbusiness/zui/IconButton';

import ChangeBillingIntervalAnnualComparison from './ChangeBillingIntervalAnnualComparison';
import ChangeBillingIntervalAnnualConfirmation from './ChangeBillingIntervalAnnualConfirmation';
import ChangeBillingIntervalAnnualReview from './ChangeBillingIntervalAnnualReview';
import ContactCS from './ContactCS';
import { baseModalContext } from '../contexts/ChangeRequestContext';
import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';

export default function ChangeBillingIntervalAnnualModalFlow({
  subscription,
  businessEntity,
  serviceDisplayName,
  products,
  billingChangeSavings,
  refetchSubscriptions
}) {
  const { formatMessage } = useIntl();
  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();
  const { modalFlow } = changeFlow;
  const { changeBillingIntervalAnnual } = modalFlow;

  const handleCloseModalFlow = () => {
    if (changeBillingIntervalAnnual.stepThree && refetchSubscriptions) {
      refetchSubscriptions();
    }
    setChangeFlow(() => baseModalContext);
  };

  return (
    <Dialog
      open={changeBillingIntervalAnnual.start}
      maxWidth="sm"
      fullWidth
      onClose={handleCloseModalFlow}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            icon={<CloseIcon />}
            label={formatMessage({ id: 'dashboard.subscriptions.billingChange.close' })}
            clickstreamProps={{ elementName: 'billing_change_modal_close' }}
            onClick={handleCloseModalFlow}
          />
        </Box>
      </DialogTitle>
      {changeBillingIntervalAnnual.stepOne && (
        <ChangeBillingIntervalAnnualComparison
          subscription={subscription}
          serviceDisplayName={serviceDisplayName}
          products={products}
          billingChangeSavings={billingChangeSavings}
        />
      )}
      {changeBillingIntervalAnnual.stepTwo && (
        <ChangeBillingIntervalAnnualReview
          subscription={subscription}
          businessEntityName={businessEntity?.name}
          serviceDisplayName={serviceDisplayName}
          products={products}
        />
      )}
      {changeBillingIntervalAnnual.stepThree && (
        <ChangeBillingIntervalAnnualConfirmation onClose={handleCloseModalFlow} />
      )}
      {modalFlow.error && (
        <ContactCS />
      )}
    </Dialog>
  );
}

ChangeBillingIntervalAnnualModalFlow.propTypes = {
  subscription: PropTypes.shape({
    orderBillingItemUuid: PropTypes.string,
    service: PropTypes.string,
    period: PropTypes.string,
    termEndDate: PropTypes.string,
    remainingProrateValue: PropTypes.number
  }).isRequired,
  businessEntity: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  serviceDisplayName: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({
    pricing: PropTypes.shape({
      uuid: PropTypes.string,
      interval: PropTypes.string,
      amount: PropTypes.number
    })
  })),
  billingChangeSavings: PropTypes.number,
  refetchSubscriptions: PropTypes.func
};
